import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { discussionsApi } from '@/lib/api';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { MessageSquare, Heart, ChevronDown, ChevronUp, Eye } from 'lucide-react';
import TimeAgo from '@/components/app/TimeAgo';
import { toast } from 'sonner';

// ─── Constants ────────────────────────────────────────────────────────────────

const POST_LIMIT = 800;
const REPLY_LIMIT = 500;

const TAG_OPTIONS = [
  { value: 'no_spoilers', label: 'No Spoilers' },
  { value: 'midway', label: 'Midway' },
  { value: 'spoilers', label: 'Spoilers' },
];

const TAG_STYLE = {
  no_spoilers: 'text-forest bg-forest/5 border-forest/20',
  midway: 'text-amber-700 bg-amber-50 border-amber-200',
  spoilers: 'text-red-600 bg-red-50 border-red-200',
};

const SHELF_BADGE = {
  read: { label: 'Read it', className: 'text-forest bg-forest/5 border-forest/20' },
  reading: { label: 'Reading now', className: 'text-terra bg-terra/5 border-terra/20' },
  want: { label: 'Wants to read', className: 'text-ink-muted bg-ink/5 border-ink/10' },
  own: { label: 'Owns it', className: 'text-ink-muted bg-ink/5 border-ink/10' },
};

function getInitials(name) {
  if (!name) return '?';
  return name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2);
}

// ─── Reply item ───────────────────────────────────────────────────────────────

function ReplyItem({ reply, onLike }) {
  const navigate = useNavigate();
  const [liked, setLiked] = useState(reply.liked);
  const [likeCount, setLikeCount] = useState(reply.like_count);

  const handleLike = async () => {
    const next = !liked;
    setLiked(next);
    setLikeCount((c) => c + (next ? 1 : -1));
    try {
      await discussionsApi.toggleLike('reply', reply.id);
    } catch (_) {
      setLiked(!next);
      setLikeCount((c) => c + (next ? -1 : 1));
    }
  };

  return (
    <div className="flex gap-2.5 pl-3 border-l-2 border-cream-dark">
      <button onClick={() => navigate(`/profile/${reply.author?.username}`)}>
        <Avatar className="w-7 h-7 flex-shrink-0">
          <AvatarImage src={reply.author?.avatar_url} />
          <AvatarFallback className="bg-terra text-cream text-xs">
            {getInitials(reply.author?.display_name || reply.author?.username)}
          </AvatarFallback>
        </Avatar>
      </button>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-1.5 flex-wrap">
          <button
            onClick={() => navigate(`/profile/${reply.author?.username}`)}
            className="text-xs font-semibold text-ink hover:text-terra transition-colors"
          >
            {reply.author?.display_name || reply.author?.username}
          </button>
          <TimeAgo date={reply.created_at} className="text-xs text-ink-muted" />
        </div>
        <p className="text-sm text-ink-light leading-relaxed mt-0.5">{reply.body}</p>
        <button
          onClick={handleLike}
          className={`flex items-center gap-1 mt-1 text-xs transition-colors ${liked ? 'text-terra' : 'text-ink-muted hover:text-terra'}`}
        >
          <Heart className={`w-3 h-3 ${liked ? 'fill-terra' : ''}`} />
          {likeCount > 0 && <span>{likeCount}</span>}
        </button>
      </div>
    </div>
  );
}

// ─── Discussion post ──────────────────────────────────────────────────────────

function DiscussionPost({ post, bookId, user, myShelfState }) {
  const navigate = useNavigate();
  const [liked, setLiked] = useState(post.liked);
  const [likeCount, setLikeCount] = useState(post.like_count);
  const [replyCount, setReplyCount] = useState(post.reply_count);
  const [showReplies, setShowReplies] = useState(false);
  const [replies, setReplies] = useState([]);
  const [loadingReplies, setLoadingReplies] = useState(false);
  const [replyBody, setReplyBody] = useState('');
  const [submittingReply, setSubmittingReply] = useState(false);
  const [spoilerRevealed, setSpoilerRevealed] = useState(false);
  const replyRef = useRef(null);

  const isSpoiler = post.tag === 'spoilers';

  const handleLike = async () => {
    if (!user) { toast.error('Sign in to like posts'); return; }
    const next = !liked;
    setLiked(next);
    setLikeCount((c) => c + (next ? 1 : -1));
    try {
      await discussionsApi.toggleLike('post', post.id);
    } catch (_) {
      setLiked(!next);
      setLikeCount((c) => c + (next ? -1 : 1));
    }
  };

  const handleToggleReplies = async () => {
    if (!showReplies && replies.length === 0) {
      setLoadingReplies(true);
      try {
        const data = await discussionsApi.getReplies(post.id);
        setReplies(data);
      } catch (_) {
        toast.error('Could not load replies');
      } finally {
        setLoadingReplies(false);
      }
    }
    setShowReplies((v) => !v);
    if (!showReplies) setTimeout(() => replyRef.current?.focus(), 100);
  };

  const handleSubmitReply = async () => {
    if (!replyBody.trim()) return;
    setSubmittingReply(true);
    try {
      const reply = await discussionsApi.createReply(post.id, bookId, { body: replyBody.trim() });
      setReplies((prev) => [...prev, { ...reply, author: { username: user.username, display_name: user.display_name, avatar_url: user.avatar_url }, like_count: 0, liked: false }]);
      setReplyCount((c) => c + 1);
      setReplyBody('');
    } catch (err) {
      toast.error(err.message || 'Could not post reply');
    } finally {
      setSubmittingReply(false);
    }
  };

  const shelfBadge = SHELF_BADGE[post.author_shelf_state];
  const tagLabel = TAG_OPTIONS.find((t) => t.value === post.tag)?.label;

  return (
    <div className="bg-cream border border-cream-dark rounded-xl p-4">
      {/* Author row */}
      <div className="flex items-start gap-3">
        <button onClick={() => navigate(`/profile/${post.author?.username}`)}>
          <Avatar className="w-8 h-8 flex-shrink-0">
            <AvatarImage src={post.author?.avatar_url} />
            <AvatarFallback className="bg-terra text-cream text-xs">
              {getInitials(post.author?.display_name || post.author?.username)}
            </AvatarFallback>
          </Avatar>
        </button>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <button
              onClick={() => navigate(`/profile/${post.author?.username}`)}
              className="text-sm font-semibold text-ink hover:text-terra transition-colors"
            >
              {post.author?.display_name || post.author?.username}
            </button>
            {shelfBadge && (
              <span className={`text-xs px-1.5 py-0.5 rounded-full border ${shelfBadge.className}`}>
                {shelfBadge.label}
              </span>
            )}
            <TimeAgo date={post.created_at} className="text-xs text-ink-muted" />
          </div>

          {/* Tag */}
          {post.tag !== 'no_spoilers' && (
            <span className={`inline-block mt-1 text-xs px-2 py-0.5 rounded-full border ${TAG_STYLE[post.tag]}`}>
              {tagLabel}
            </span>
          )}

          {/* Body — spoiler blur */}
          <div className="mt-2 relative">
            <p className={`text-sm text-ink-light leading-relaxed transition-all ${isSpoiler && !spoilerRevealed ? 'blur-sm select-none' : ''}`}>
              {post.body}
            </p>
            {isSpoiler && !spoilerRevealed && (
              <div className="absolute inset-0 flex items-center justify-center">
                <button
                  onClick={() => setSpoilerRevealed(true)}
                  className="flex items-center gap-1.5 bg-ink text-cream text-xs font-medium px-3 py-1.5 rounded-lg hover:bg-ink/80 transition-colors"
                >
                  <Eye className="w-3.5 h-3.5" />
                  Reveal Spoiler
                </button>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-4 mt-3">
            <button
              onClick={handleLike}
              className={`flex items-center gap-1 text-xs transition-colors ${liked ? 'text-terra' : 'text-ink-muted hover:text-terra'}`}
            >
              <Heart className={`w-3.5 h-3.5 ${liked ? 'fill-terra' : ''}`} />
              <span>{likeCount > 0 ? likeCount : ''} Like</span>
            </button>
            <button
              onClick={handleToggleReplies}
              className="flex items-center gap-1 text-xs text-ink-muted hover:text-terra transition-colors"
            >
              <MessageSquare className="w-3.5 h-3.5" />
              <span>{replyCount > 0 ? replyCount : ''} {replyCount === 1 ? 'Reply' : 'Replies'}</span>
              {showReplies ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            </button>
          </div>
        </div>
      </div>

      {/* Replies section */}
      {showReplies && (
        <div className="mt-4 space-y-3">
          {loadingReplies ? (
            <div className="space-y-2 pl-3">
              {[...Array(2)].map((_, i) => <Skeleton key={i} className="h-10 bg-cream-dark rounded-lg" />)}
            </div>
          ) : (
            replies.map((reply) => (
              <ReplyItem key={reply.id} reply={reply} />
            ))
          )}

          {user && (
            <div className="flex gap-2 pt-1">
              <Avatar className="w-7 h-7 flex-shrink-0 mt-1">
                <AvatarImage src={user.avatar_url} />
                <AvatarFallback className="bg-terra text-cream text-xs">
                  {getInitials(user.display_name || user.username)}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <Textarea
                  ref={replyRef}
                  value={replyBody}
                  onChange={(e) => setReplyBody(e.target.value.slice(0, REPLY_LIMIT))}
                  placeholder="Write a reply…"
                  className="bg-cream-dark border-cream-dark focus:border-terra text-ink placeholder:text-ink-muted text-sm min-h-[60px] resize-none"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleSubmitReply();
                  }}
                />
                <div className="flex items-center justify-between mt-1.5">
                  <span className="text-xs text-ink-muted">{replyBody.length}/{REPLY_LIMIT}</span>
                  <Button
                    size="sm"
                    onClick={handleSubmitReply}
                    disabled={submittingReply || !replyBody.trim()}
                    className="bg-terra hover:bg-terra/90 text-cream text-xs h-7"
                  >
                    {submittingReply ? 'Posting…' : 'Reply'}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Main DiscussionTab ───────────────────────────────────────────────────────

export default function DiscussionTab({ bookId, user, myShelfState }) {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [body, setBody] = useState('');
  const [tag, setTag] = useState('no_spoilers');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!bookId) return;
    setLoading(true);
    discussionsApi.getPosts(bookId)
      .then(setPosts)
      .catch(() => setPosts([]))
      .finally(() => setLoading(false));
  }, [bookId]);

  const handleSubmitPost = async () => {
    if (!body.trim()) return;
    setSubmitting(true);
    try {
      const post = await discussionsApi.createPost(bookId, { body: body.trim(), tag });
      setPosts((prev) => [{
        ...post,
        author: {
          username: user.username,
          display_name: user.display_name,
          avatar_url: user.avatar_url,
        },
        like_count: 0,
        reply_count: 0,
        liked: false,
        author_shelf_state: myShelfState || null,
      }, ...prev]);
      setBody('');
      setTag('no_spoilers');
      toast.success('Posted!');
    } catch (err) {
      toast.error(err.message || 'Could not post');
    } finally {
      setSubmitting(false);
    }
  };

  if (!bookId) return null;

  return (
    <div className="space-y-4">
      {/* Post creation form */}
      {user ? (
        <div className="bg-cream border border-cream-dark rounded-xl p-4 space-y-3">
          <Textarea
            value={body}
            onChange={(e) => setBody(e.target.value.slice(0, POST_LIMIT))}
            placeholder="Share your thoughts on this book…"
            className="bg-cream-dark border-cream-dark focus:border-terra text-ink placeholder:text-ink-muted min-h-[80px] resize-none text-sm"
          />
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              {/* Tag selector */}
              <div className="flex gap-1.5">
                {TAG_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => setTag(opt.value)}
                    className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                      tag === opt.value
                        ? TAG_STYLE[opt.value]
                        : 'border-cream-dark text-ink-muted hover:border-terra/40'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <span className="text-xs text-ink-muted">{body.length}/{POST_LIMIT}</span>
              <Button
                size="sm"
                onClick={handleSubmitPost}
                disabled={submitting || !body.trim()}
                className="bg-terra hover:bg-terra/90 text-cream text-xs"
              >
                {submitting ? 'Posting…' : 'Post'}
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <p className="text-sm text-ink-muted text-center py-4">
          <a href="/login" className="text-terra hover:underline">Sign in</a> to join the discussion.
        </p>
      )}

      {/* Post list */}
      {loading ? (
        <div className="space-y-3">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} className="h-28 bg-cream-dark rounded-xl" />
          ))}
        </div>
      ) : posts.length === 0 ? (
        <div className="text-center py-12">
          <MessageSquare className="w-10 h-10 text-ink-muted mx-auto mb-3 opacity-40" />
          <p className="text-ink-muted text-sm">No discussion yet. Start one!</p>
        </div>
      ) : (
        posts.map((post) => (
          <DiscussionPost
            key={post.id}
            post={post}
            bookId={bookId}
            user={user}
            myShelfState={myShelfState}
          />
        ))
      )}
    </div>
  );
}
